/**
 * Erfassungsliste für Wareneingang und Inventur (Reiter „Lager“): Scans von
 * Tastatur-Scanner, MDE-Gerät oder Kamera sammeln sich je Code; erst „Buchen“
 * schickt die Liste an den Server. Die Liste überlebt einen Neustart des Geräts
 * (localStorage), damit ein halber Wareneingang nicht verloren geht.
 */
import type { Speicher } from './belegnummern';
import { neueVorgangsId } from './bonierung';

export type ErfassungsArt = 'wareneingang' | 'inventur';

export interface ErfassteZeile {
  code: string;
  menge: number;
  /** Anzeige: Artikel, Vorschlag oder Produktdatenbank; leer = noch unbekannt. */
  bezeichnung: string;
  /** Artikel bekannt (sonst wird beim Buchen ein Vorschlag daraus). */
  bekannt: boolean;
  /** Aktueller Bestand laut Server beim Scannen (nur bekannte Artikel mit Bestandsführung). */
  bestand: number | null;
  /** Sperrgrund des Artikels (gesperrt für den Verkauf, im Lager trotzdem buchbar). */
  sperrgrund?: string | null;
}

/**
 * Code aus Scanner oder Eingabe: Text wie im Artikelstamm (Migration 0050) – 1–48 druckbare
 * Zeichen ohne Leerzeichen. Keine EAN-Pflicht: interne Kurzcodes, Artikelnummern und
 * Barcodes mit abweichender Prüfziffer gehören dazu. Zugeordnet wird am Server
 * (lagercode.ts: exakt Barcode → EAN → Artikelnummer, danach EAN-Normalisierung).
 */
export function pruefeCode(roh: string): string | null {
  const c = roh.trim();
  return /^[!-~]{1,48}$/.test(c) ? c : null;
}

export interface Erfassung {
  art: ErfassungsArt;
  bezug: string;
  zeilen: ErfassteZeile[];
  /**
   * Vorgangskennung, unter der diese Liste gebucht wird – liegt mit der Liste im
   * localStorage. Ist ein Auftrag mit dieser Kennung schon abgelegt, ist die Liste (bis auf
   * später Dazugescanntes) bereits übergeben; so entsteht nie eine zweite Kennung für
   * dieselbe Liste (Absturz zwischen Ablage und Leeren, zweiter Tab).
   */
  vorgangId?: string;
  /**
   * Aufträge, deren nicht gebuchte Zeilen schon in diese Liste zurückgeholt wurden – liegt im
   * selben localStorage-Eintrag wie die Zeilen, eine Übernahme wirkt so höchstens einmal.
   */
  uebernommeneFehler?: string[];
  /**
   * Laufanker vom ersten Scan: höchste damals bekannte Inventurlauf-ID und der damals offene
   * Lauf. Eine Inventurliste darf nie über einen Lauf hinweg gebucht werden – der Server prüft
   * das mit demselben Anker (wareneingang.ts `freieInventurVerboten`, Zählroute). Fehlt er,
   * ist der Laufstand beim Beginn unbekannt (vorsichtigste Annahme).
   */
  laufAnker?: LaufAnker;
}

/** Siehe Erfassung.laufAnker (gleiche Form wie am Server). */
export interface LaufAnker {
  letzteLaufId: number;
  offenLaufId: number | null;
}

function istLaufAnker(x: unknown): x is LaufAnker {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  const a = x as Record<string, unknown>;
  return (
    Number.isSafeInteger(a.letzteLaufId) &&
    (a.letzteLaufId as number) >= 0 &&
    (a.offenLaufId === null ||
      (Number.isSafeInteger(a.offenLaufId) && (a.offenLaufId as number) > 0))
  );
}

/**
 * Zwei Anker derselben Liste (z. B. zurückgeholte Fehlerzeilen in eine laufende Liste):
 * unbekannt oder verschiedene Läufe → unbekannt; sonst der ältere Stand.
 */
export function ankerVereinen(
  a: LaufAnker | undefined,
  b: LaufAnker | undefined,
): LaufAnker | undefined {
  if (a === undefined || b === undefined) {
    return undefined;
  }
  if (a.offenLaufId !== b.offenLaufId) {
    return undefined;
  }
  return { letzteLaufId: Math.min(a.letzteLaufId, b.letzteLaufId), offenLaufId: a.offenLaufId };
}

/** Liste mit diesem Anker (undefined = ohne Anker). */
function mitAnker(e: Erfassung, anker: LaufAnker | undefined): Erfassung {
  const rest = { ...e };
  delete rest.laufAnker;
  return anker === undefined ? rest : { ...rest, laufAnker: anker };
}

export const LEERE_ERFASSUNG: Erfassung = { art: 'wareneingang', bezug: '', zeilen: [] };

/** EAN-8/EAN-13 mit Prüfziffer (Gewichte 3,1,3,… von rechts). */
export function istGueltigeEan(ean: string): boolean {
  if (!/^(\d{8}|\d{13})$/.test(ean)) {
    return false;
  }
  const ziffern = Array.from({ length: ean.length }, (_, i) => Number(ean.charAt(i)));
  const pruef = ziffern.pop() ?? 0;
  const summe = ziffern.reverse().reduce((s, z, i) => s + z * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (summe % 10)) % 10 === pruef;
}

/** Wie der Server: UPC-A (12) und GTIN-14 mit führender 0 werden zur EAN-13. */
export function normalisiereEan(code: string): string | null {
  let c = code.replace(/\s+/g, '');
  if (/^\d{12}$/.test(c)) {
    c = `0${c}`;
  } else if (/^0\d{13}$/.test(c)) {
    c = c.slice(1);
  }
  return istGueltigeEan(c) ? c : null;
}

export function rundeMenge(m: number): number {
  return Math.round(m * 1000) / 1000;
}

/**
 * Scan hinzufügen: gleicher Code erhöht die Menge, neuer Code kommt nach oben. Der erste
 * Scan einer (leeren) Liste setzt ihren Laufanker neu – ohne bekannten Laufstand keinen.
 */
export function scanHinzufuegen(
  e: Erfassung,
  zeile: Omit<ErfassteZeile, 'menge'>,
  menge = 1,
  anker?: LaufAnker,
): Erfassung {
  if (e.zeilen.length === 0) {
    e = mitAnker(e, anker);
  }
  const vorhanden = e.zeilen.find((z) => z.code === zeile.code);
  if (vorhanden !== undefined) {
    return {
      ...e,
      zeilen: [
        { ...vorhanden, ...zeile, menge: rundeMenge(vorhanden.menge + menge) },
        ...e.zeilen.filter((z) => z.code !== zeile.code),
      ],
    };
  }
  return { ...e, zeilen: [{ ...zeile, menge: rundeMenge(menge) }, ...e.zeilen] };
}

export function mengeSetzen(e: Erfassung, code: string, menge: number): Erfassung {
  return {
    ...e,
    zeilen: e.zeilen.map((z) => (z.code === code ? { ...z, menge: rundeMenge(menge) } : z)),
  };
}

export function zeileEntfernen(e: Erfassung, code: string): Erfassung {
  return ohneAnkerWennLeer({ ...e, zeilen: e.zeilen.filter((z) => z.code !== code) });
}

/** Eine leere Liste hat keinen Anker – die nächste Zählung beginnt neu. */
function ohneAnkerWennLeer(e: Erfassung): Erfassung {
  return e.zeilen.length > 0 ? e : mitAnker(e, undefined);
}

/** Menge aus der Eingabe („2,5“ oder „12“); null bei Unsinn. */
export function leseMenge(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,3})?$/.test(t)) {
    return null;
  }
  return Number(t);
}

function schluessel(kassenId: string): string {
  return `bilanx.erfassung.${kassenId}`;
}

export function ladeErfassung(speicher: Speicher, kassenId: string): Erfassung {
  const roh = speicher.getItem(schluessel(kassenId));
  if (roh === null) {
    return LEERE_ERFASSUNG;
  }
  try {
    const e = JSON.parse(roh) as Partial<Erfassung>;
    if (
      (e.art === 'wareneingang' || e.art === 'inventur') &&
      typeof e.bezug === 'string' &&
      Array.isArray(e.zeilen)
    ) {
      return {
        art: e.art,
        bezug: e.bezug,
        ...(typeof e.vorgangId === 'string' && e.vorgangId !== ''
          ? { vorgangId: e.vorgangId }
          : {}),
        ...(istLaufAnker(e.laufAnker)
          ? {
              laufAnker: {
                letzteLaufId: e.laufAnker.letzteLaufId,
                offenLaufId: e.laufAnker.offenLaufId,
              },
            }
          : {}),
        ...(Array.isArray(e.uebernommeneFehler)
          ? {
              uebernommeneFehler: e.uebernommeneFehler.filter(
                (x): x is string => typeof x === 'string',
              ),
            }
          : {}),
        zeilen: e.zeilen.filter(
          (z): z is ErfassteZeile =>
            typeof z === 'object' &&
            typeof z.code === 'string' &&
            typeof z.menge === 'number' &&
            typeof z.bezeichnung === 'string',
        ),
      };
    }
  } catch {
    // kaputter Eintrag – neu beginnen
  }
  return LEERE_ERFASSUNG;
}

export function speichereErfassung(speicher: Speicher, kassenId: string, e: Erfassung): void {
  speicher.setItem(schluessel(kassenId), JSON.stringify(e));
}

/**
 * Nach einer Buchung: von jeder gesendeten Zeile genau die gesendete Menge abziehen;
 * fehlerhafte Zeilen (Code in `fehler`) bleiben vollständig. Was während der
 * Buchung dazugescannt wurde (höhere Menge oder neuer Code), bleibt stehen.
 */
export function ohneGebuchte(
  zeilen: readonly ErfassteZeile[],
  gesendet: readonly { code: string; menge: number }[],
  fehler: ReadonlySet<string>,
): ErfassteZeile[] {
  const rest: ErfassteZeile[] = [];
  for (const z of zeilen) {
    const s = gesendet.find((g) => g.code === z.code);
    if (s === undefined || fehler.has(z.code)) {
      rest.push(z);
      continue;
    }
    const menge = rundeMenge(z.menge - s.menge);
    if (menge > 0) {
      rest.push({ ...z, menge });
    }
  }
  return rest;
}

interface SitzungsSpeicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
  removeItem(schluessel: string): void;
}

function vorgangSchluessel(kassenId: string): string {
  return `bilanx.lager.vorgang.${kassenId}`;
}

/**
 * Vorgangskennung einer Buchung: Für dieselbe Liste (Inhalt) bleibt sie gleich, bis die
 * Buchung bestätigt ist – ein erneuter Versuch nach verlorener Antwort bucht nicht doppelt.
 */
export function vorgangFuer(
  speicher: SitzungsSpeicher,
  kassenId: string,
  inhalt: string,
  neueId: () => string = neueVorgangsId,
): string {
  const roh = speicher.getItem(vorgangSchluessel(kassenId));
  if (roh !== null) {
    try {
      const v = JSON.parse(roh) as { id?: unknown; inhalt?: unknown };
      if (typeof v.id === 'string' && v.inhalt === inhalt) {
        return v.id;
      }
    } catch {
      // unlesbar → neue Kennung
    }
  }
  const id = neueId();
  speicher.setItem(vorgangSchluessel(kassenId), JSON.stringify({ id, inhalt }));
  return id;
}

/** Buchung bestätigt: nächste Liste bekommt eine neue Kennung. */
export function vorgangErledigt(speicher: SitzungsSpeicher, kassenId: string): void {
  speicher.removeItem(vorgangSchluessel(kassenId));
}

/** Liste mit Vorgangskennung (ältere gespeicherte Listen haben noch keine). */
export function mitKennung(e: Erfassung, neueId: () => string = neueVorgangsId): Erfassung {
  return e.vorgangId === undefined ? { ...e, vorgangId: neueId() } : e;
}

/**
 * Übergang von der alten Sitzungskennung (bis 2026-10 lag die Vorgangskennung nur im
 * sessionStorage): Ging eine Buchung mit genau diesem Inhalt schon an den Server und kam
 * keine Antwort, übernimmt der neue Buchungsauftrag diese Kennung – sonst null. Nur
 * lesen; entfernt wird der Eintrag erst nach der dauerhaften Ablage
 * ({@link vergissAlteVorgangsId}), sonst ginge die Kennung bei einem Fehler verloren.
 */
export function alteVorgangsId(
  speicher: SitzungsSpeicher,
  kassenId: string,
  inhalt: string,
): string | null {
  const roh = speicher.getItem(vorgangSchluessel(kassenId));
  if (roh === null) {
    return null;
  }
  try {
    const v = JSON.parse(roh) as { id?: unknown; inhalt?: unknown };
    return typeof v.id === 'string' && v.inhalt === inhalt ? v.id : null;
  } catch {
    return null;
  }
}

/** Alte Sitzungskennung entfernen – erst, wenn der Auftrag dauerhaft abgelegt ist. */
export function vergissAlteVorgangsId(speicher: SitzungsSpeicher, kassenId: string): void {
  speicher.removeItem(vorgangSchluessel(kassenId));
}

/**
 * Liste nach der Übergabe eines Auftrags (auch, wenn der Auftrag mit der Kennung der Liste
 * schon abgelegt war: Absturz, zweiter Tab); danach neue Kennung.
 *  - Wareneingang: genau die übergebenen Mengen abziehen – was dazugescannt wurde, bleibt.
 *  - Inventur: Mengen sind absolute Zählstände. Eine Zeile mit unveränderter Menge ist
 *    übergeben und fällt weg; wurde sie inzwischen weitergezählt, bleibt der neue Zählstand
 *    vollständig stehen (abziehen ergäbe einen falschen absoluten Bestand).
 */
export function listeNachUebergabe(
  e: Erfassung,
  auftrag: { art: ErfassungsArt; positionen: readonly { code: string; menge: number }[] },
  neueId: () => string = neueVorgangsId,
): Erfassung {
  const zeilen =
    auftrag.art === 'inventur'
      ? e.zeilen.filter((z) => {
          const p = auftrag.positionen.find((x) => x.code === z.code);
          return p === undefined || rundeMenge(p.menge) !== rundeMenge(z.menge);
        })
      : ohneGebuchte(e.zeilen, auftrag.positionen, new Set());
  return ohneAnkerWennLeer({
    ...e,
    bezug: zeilen.length > 0 ? e.bezug : '',
    zeilen,
    vorgangId: neueId(),
  });
}

/**
 * Nicht gebuchte Zeilen eines Auftrags höchstens einmal in die Liste übernehmen (die
 * Auftragskennung wird mit der Liste gespeichert; die letzten 50 genügen).
 */
export function fehlerZurueckEinmal(
  e: Erfassung,
  vorgangId: string,
  zeilen: readonly { code: string; menge: number }[],
  /** Laufanker des Auftrags, aus dem die Zeilen stammen. */
  anker?: LaufAnker,
): Erfassung {
  const schon = e.uebernommeneFehler ?? [];
  if (schon.includes(vorgangId)) {
    return e;
  }
  return {
    ...zeilenZurueck(e, zeilen, anker),
    uebernommeneFehler: [...schon, vorgangId].slice(-50),
  };
}

/**
 * Nicht gebuchte Zeilen (Fehler) wieder in die Liste übernehmen. Sie stammen aus einer früheren
 * Zählung: in eine leere Liste kommt deren Anker, in eine laufende der vorsichtigere (ankerVereinen).
 */
export function zeilenZurueck(
  e: Erfassung,
  zeilen: readonly { code: string; menge: number }[],
  anker?: LaufAnker,
): Erfassung {
  const leer = e.zeilen.length === 0;
  const ergebnis = zeilen.reduce(
    (acc, z) =>
      scanHinzufuegen(
        acc,
        { code: z.code, bezeichnung: '', bekannt: false, bestand: null },
        z.menge,
        anker,
      ),
    e,
  );
  if (zeilen.length === 0) {
    return ergebnis;
  }
  return mitAnker(ergebnis, leer ? anker : ankerVereinen(e.laufAnker, anker));
}

export type InventurZiel =
  { art: 'frei' } | { art: 'lauf'; laufId: number } | { art: 'gesperrt'; grund: string };

/** Laufstand des Unternehmers, wie ihn Gerät bzw. Kasse frisch vom Server holen. */
export interface InventurStand {
  letzteLaufId: number;
  offen: { id: number; bezeichnung: string } | null;
}

/** Anker für eine neue Liste aus dem aktuellen Stand. */
export function ankerAus(stand: InventurStand): LaufAnker {
  return { letzteLaufId: stand.letzteLaufId, offenLaufId: stand.offen?.id ?? null };
}

/**
 * Wohin darf eine Inventurliste mit diesem Laufanker (fehlt = unbekannt)? Gleiche Regeln wie
 * am Server (wareneingang.ts `freieInventurVerboten`, Zählroute) – hier nur, um eine sichere
 * Abweisung schon vor dem Senden zu erklären; ohne Uhrzeiten, nur über Lauf-IDs:
 *  - offener Lauf: in den Lauf nur, wenn die Liste in genau diesem Lauf begann;
 *  - kein offener Lauf: freie Inventur nur, wenn die Liste ohne offenen Lauf begann und seither
 *    kein Lauf begann; ohne Anker nur, wenn es nie einen Lauf gab.
 */
export function inventurZiel(stand: InventurStand, anker: LaufAnker | undefined): InventurZiel {
  if (stand.offen !== null) {
    if (anker?.offenLaufId === stand.offen.id) {
      return { art: 'lauf', laufId: stand.offen.id };
    }
    return {
      art: 'gesperrt',
      grund: `Diese Liste wurde nicht in Inventurlauf ${String(stand.offen.id)} begonnen – für den Lauf bitte neu zählen (Liste leeren).`,
    };
  }
  if (anker === undefined) {
    return stand.letzteLaufId === 0
      ? { art: 'frei' }
      : {
          art: 'gesperrt',
          grund:
            'Ob während dieser Zählung ein Inventurlauf lief, ist nicht bekannt – nicht als freie Inventur buchen. Liste leeren und neu zählen.',
        };
  }
  if (anker.offenLaufId !== null) {
    return {
      art: 'gesperrt',
      grund: `Diese Liste gehört zu Inventurlauf ${String(anker.offenLaufId)}, der nicht mehr offen ist – nicht als freie Inventur buchen. Liste leeren und neu zählen.`,
    };
  }
  if (stand.letzteLaufId > anker.letzteLaufId) {
    return {
      art: 'gesperrt',
      grund: `Während dieser Zählung lief Inventurlauf ${String(stand.letzteLaufId)} – nicht als freie Inventur buchen. Liste leeren und neu zählen.`,
    };
  }
  return { art: 'frei' };
}

/**
 * A5-6a: Code mit GTIN-Länge (EAN-8, UPC-A, EAN-13, GTIN-14) aus Ziffern, aber mit falscher
 * Prüfziffer (Gewichte 3, 1, 3, … von rechts, wie `istGueltigeEan`).
 */
export function gtinPruefzifferFalsch(code: string): boolean {
  if (!/^\d+$/.test(code) || ![8, 12, 13, 14].includes(code.length)) {
    return false;
  }
  const ziffern = Array.from(code, Number);
  const pruef = ziffern.pop() ?? 0;
  const summe = ziffern.reverse().reduce((s, z, i) => s + z * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (summe % 10)) % 10 !== pruef;
}

/**
 * A5-6a (Testserver 01.11.): Ein Scan mit falscher Prüfziffer erschien als „neu“, der Server
 * lehnte ihn beim Buchen ab (keine gültige EAN für einen Vorschlag). Jetzt vorab:
 *  - Code ist einem Artikel zugeordnet (der Stamm erlaubt solche Codes, wie sie auf der Ware
 *    stehen): kein Einwand;
 *  - Server kennt ihn nicht: abweisen (nicht in die Liste);
 *  - ohne Auskunft (offline): aufnehmen, aber warnen – ob ein Artikel ihn trägt, klärt das Buchen.
 */
export function pruefzifferEinwand(
  code: string,
  auskunft: { artikel: unknown } | null,
): { art: 'abweisen' | 'warnen'; text: string } | null {
  if (!gtinPruefzifferFalsch(code) || (auskunft !== null && auskunft.artikel !== null)) {
    return null;
  }
  return auskunft === null
    ? {
        art: 'warnen',
        text: `${code}: ungültige Prüfziffer – nur buchbar, wenn ein Artikel genau diesen Code trägt. Bitte Code prüfen.`,
      }
    : {
        art: 'abweisen',
        text: `${code}: ungültige Prüfziffer – kein gültiger EAN-/GTIN-Code, nicht erfasst. Bitte neu scannen bzw. Code prüfen.`,
      };
}

/** A5-6b: Beschriftung des Bezugsfelds der Liste (die Zählstelle ist ein Ort, keine Menge). */
export function bezugBeschriftung(art: ErfassungsArt): string {
  return art === 'wareneingang' ? 'Lieferschein' : 'Zählstelle (Ort, z. B. Regal 3)';
}
